import * as z from "zod";
import {
  CorrelatedCarrierServiceSchema,
  GofoServices,
  ServiceSchema,
  UniUniServices,
} from "./carrier-service-catalog";
import {
  Carriers,
  DhlEcsServices,
  UpsServices,
  UspsServices,
} from "./carrier-service-enums";

export const DynamicRatePricingEnabledCarriers = z.enum([
  Carriers.DHL_ECS,
  Carriers.UNIUNI,
  Carriers.GOFO,
]);

const dynamicRatePricingRuleSchema = z
  .object({
    carrier: DynamicRatePricingEnabledCarriers,
    service: ServiceSchema,
    benchmarks: z.array(CorrelatedCarrierServiceSchema).min(1),
    minBeatRateBy: z.number().positive().multipleOf(0.01),
    maxBeatRateBy: z.number().positive().multipleOf(0.01),
  })
  .refine((rule) => CorrelatedCarrierServiceSchema.safeParse(rule).success, {
    message: "Select a service supported by the source carrier",
    path: ["service"],
  })
  .refine((rule) => rule.minBeatRateBy <= rule.maxBeatRateBy, {
    message: "Minimum savings must not exceed maximum savings",
    path: ["minBeatRateBy"],
  });

export type DynamicRatePricingRule = z.infer<
  typeof dynamicRatePricingRuleSchema
>;
export const DynamicRatePricingRuleSchema: z.ZodType<DynamicRatePricingRule> =
  dynamicRatePricingRuleSchema;

const groundBenchmarks: DynamicRatePricingRule["benchmarks"] = [
  { carrier: Carriers.USPS, service: UspsServices.GroundAdvantage },
  { carrier: Carriers.UPS, service: UpsServices.Ground },
  { carrier: Carriers.UPS_SUREPOST, service: UpsServices.SurePostUnder1Lb },
  { carrier: Carriers.UPS_SUREPOST, service: UpsServices.SurePostOver1Lb },
];
const expeditedBenchmarks: DynamicRatePricingRule["benchmarks"] = [
  { carrier: Carriers.USPS, service: UspsServices.Priority },
  { carrier: Carriers.UPS, service: UpsServices.ThreeDaySelect },
];
const printedMatterBenchmarks: DynamicRatePricingRule["benchmarks"] = [
  { carrier: Carriers.USPS, service: UspsServices.MediaMail },
  { carrier: Carriers.UPS, service: UpsServices.Ground },
];
const dhlBenchmarks: Record<
  DhlEcsServices,
  DynamicRatePricingRule["benchmarks"] | null
> = {
  [DhlEcsServices.DhlParcelGround]: groundBenchmarks,
  [DhlEcsServices.DhlParcelExpedited]: groundBenchmarks,
  [DhlEcsServices.DhlParcelExpeditedMax]: expeditedBenchmarks,
  [DhlEcsServices.DhlBPMGround]: printedMatterBenchmarks,
  [DhlEcsServices.DhlBPMExpedited]: printedMatterBenchmarks,
  [DhlEcsServices.DhlSmartMailParcelReturnGround]: null,
  [DhlEcsServices.DhlSmartMailParcelReturnLight]: null,
  [DhlEcsServices.DhlSmartMailParcelReturnPlus]: null,
  [DhlEcsServices.DhlParcelInternationalDirect]: null,
  [DhlEcsServices.DhlParcelInternationalStandard]: null,
  [DhlEcsServices.DhlPacketInternational]: null,
  [DhlEcsServices.DhlParcelInternationalDirectPriority]: null,
  [DhlEcsServices.DhlParcelInternationalDirectStandard]: null,
  [DhlEcsServices.DhlParcelInternationalDirectSMB]: null,
  [DhlEcsServices.DhlParcelInternationalStandardSMB]: null,
};

const gofoBenchmarks: Record<
  GofoServices,
  DynamicRatePricingRule["benchmarks"] | null
> = {
  [GofoServices.ParcelPickup]: groundBenchmarks,
  [GofoServices.GroundPickup]: groundBenchmarks,
  [GofoServices.EconomyCentral]: groundBenchmarks,
  [GofoServices.EconomyEast]: groundBenchmarks,
  [GofoServices.EconomyNortheast]: groundBenchmarks,
  [GofoServices.EconomySouth]: groundBenchmarks,
  [GofoServices.EconomyWest]: groundBenchmarks,
  [GofoServices.ExpeditedNortheast]: expeditedBenchmarks,
  [GofoServices.ExpeditedWest]: expeditedBenchmarks,
  [GofoServices.ExpeditedNewJersey]: expeditedBenchmarks,
  [GofoServices.RegionalCentral]: groundBenchmarks,
  [GofoServices.RegionalCharlotte]: groundBenchmarks,
  [GofoServices.RegionalCincinnati]: groundBenchmarks,
  [GofoServices.RegionalDenver]: groundBenchmarks,
  [GofoServices.RegionalEast]: groundBenchmarks,
  [GofoServices.RegionalNortheast]: groundBenchmarks,
  [GofoServices.RegionalNewJersey]: groundBenchmarks,
  [GofoServices.RegionalOrlando]: groundBenchmarks,
  [GofoServices.RegionalSoutheast]: groundBenchmarks,
  [GofoServices.RegionalSanFrancisco]: groundBenchmarks,
  [GofoServices.RegionalSouth]: groundBenchmarks,
  [GofoServices.RegionalWest]: groundBenchmarks,
  [GofoServices.StandardWest]: groundBenchmarks,
  [GofoServices.StandardSaltLakeCity]: groundBenchmarks,
  [GofoServices.StandardCentral]: groundBenchmarks,
  [GofoServices.StandardNewJersey]: groundBenchmarks,
  [GofoServices.StandardNortheast]: groundBenchmarks,
  [GofoServices.StandardEast]: groundBenchmarks,
  [GofoServices.StandardSoutheast]: groundBenchmarks,
  [GofoServices.StandardSouth]: groundBenchmarks,
  [GofoServices.CanadaDomesticEconomyEast]: null,
  [GofoServices.CanadaDomesticEconomyWest]: null,
  [GofoServices.UsaToCanadaEconomyNortheast]: null,
  [GofoServices.UsaToCanadaEconomyWest]: null,
  [GofoServices.UsaToEuEconomyNortheast]: null,
  [GofoServices.CanadaToUsaEconomyEast]: null,
  [GofoServices.UsaToUkEconomyNortheast]: null,
};

function defaultRule(
  carrier: DynamicRatePricingRule["carrier"],
  service: DynamicRatePricingRule["service"],
  benchmarks: DynamicRatePricingRule["benchmarks"] | null,
): DynamicRatePricingRule[] {
  return benchmarks
    ? [
        {
          carrier,
          service,
          benchmarks,
          minBeatRateBy: 0.01,
          maxBeatRateBy: 0.3,
        },
      ]
    : [];
}

export const DEFAULT_DYNAMIC_RATE_PRICING_RULES: DynamicRatePricingRule[] = [
  ...Object.values(DhlEcsServices).flatMap((service) =>
    defaultRule(Carriers.DHL_ECS, service, dhlBenchmarks[service]),
  ),
  ...Object.values(UniUniServices).flatMap((service) =>
    defaultRule(Carriers.UNIUNI, service, groundBenchmarks),
  ),
  ...Object.values(GofoServices).flatMap((service) =>
    defaultRule(Carriers.GOFO, service, gofoBenchmarks[service]),
  ),
];

const dynamicRatePricingSettingsSchema = z
  .object({
    enabled: z.boolean(),
    rules: z
      .array(DynamicRatePricingRuleSchema)
      .default(() => structuredClone(DEFAULT_DYNAMIC_RATE_PRICING_RULES)),
  })
  .refine(
    ({ rules }) =>
      new Set(rules.map((rule) => `${rule.carrier}:${rule.service}`)).size ===
      rules.length,
    {
      message: "Configure only one rule per carrier and service",
      path: ["rules"],
    },
  );

export type DynamicRatePricingSettings = z.infer<
  typeof dynamicRatePricingSettingsSchema
>;
export const DynamicRatePricingSettingsSchema: z.ZodType<DynamicRatePricingSettings> =
  dynamicRatePricingSettingsSchema;
